import React, {useCallback, useEffect, useRef} from 'react';
import {HomeReel, HomeReelFeedView} from '../../../../services/socialFeedApi';
import HomeErrorMessage from './HomeErrorMessage';
import {REEL_PLACEHOLDER_BACKGROUNDS} from '../constants';
import {
  formatDuration,
  formatViewCount,
  getAssetUrl,
  getDisplayInitial,
} from '../utils';
import styles from '../Home.module.css';

const playFillIconUrl = `${process.env.PUBLIC_URL}/icons/play-fill.svg`;
const viewFillIconUrl = `${process.env.PUBLIC_URL}/icons/view-fill.svg`;
const heartOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/heart-outline.svg`;
const commentOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/comment-outline.svg`;
const reelsHeadingIconUrl = `${process.env.PUBLIC_URL}/icons/video-two.svg`;
const reelsEmptyBackgroundUrl = `${process.env.PUBLIC_URL}/reel_background.png`;
const reelsEmptyCreateIconUrl = `${process.env.PUBLIC_URL}/icons/video-two.svg`;
const scrollArrowIconUrls = {
  left: `${process.env.PUBLIC_URL}/icons/arrow-left-bold.svg`,
  right: `${process.env.PUBLIC_URL}/icons/arrow-right-bold.svg`,
};

interface LiveReelsSectionProps {
  reels: HomeReel[];
  reelsLoading: boolean;
  reelsError: string | null;
  selectedView: HomeReelFeedView;
  searchQuery: string;
  hasMoreReels: boolean;
  loadMoreReelsLoading: boolean;
  loadMoreReelsError: string | null;
  activeReelId: string | null;
  failedReelThumbnails: Record<string, boolean>;
  onRetry: () => void;
  onActivateReel: (reelId: string) => void;
  onPlayReel: (reel: HomeReel) => void;
  onThumbnailError: (reelId: string) => void;
  onViewChange: (view: HomeReelFeedView) => void;
  onSearchChange: (value: string) => void;
  onLoadMoreReels: () => void;
  onCreateReel: () => void;
  activeTopicLabel?: string | null;
}

interface ReelCardProps {
  reel: HomeReel;
  index: number;
  isActive: boolean;
  hasThumbnailError: boolean;
  onActivate: (reelId: string) => void;
  onPlay: (reel: HomeReel) => void;
  onThumbnailError: (reelId: string) => void;
}

const REEL_FILTER_OPTIONS: Array<{id: HomeReelFeedView; label: string}> = [
  {id: 'all', label: 'All'},
  {id: 'featured', label: 'Featured'},
  {id: 'newest', label: 'Newest'},
  {id: 'popular', label: 'Popular'},
  {id: 'mostDiscussed', label: 'Most Discussed'},
];

const ReelCard: React.FC<ReelCardProps> = React.memo(({
  reel,
  index,
  isActive,
  hasThumbnailError,
  onActivate,
  onPlay,
  onThumbnailError,
}) => {
  const reelThumbnailUrl = getAssetUrl(reel.thumbnailUrl);
  const authorAvatarUrl = getAssetUrl(reel.author.profilePicture);
  const [authorAvatarFailed, setAuthorAvatarFailed] = React.useState(false);
  const showThumbnail = Boolean(reelThumbnailUrl) && !hasThumbnailError;
  const placeholderBackground = REEL_PLACEHOLDER_BACKGROUNDS[index % REEL_PLACEHOLDER_BACKGROUNDS.length];
  const formattedViewCount = formatViewCount(reel.viewCount);
  const formattedLikeCount = formatViewCount(reel.likeCount ?? 0);
  const formattedCommentCount = formatViewCount(reel.commentCount ?? 0);

  useEffect(() => {
    setAuthorAvatarFailed(false);
  }, [authorAvatarUrl]);

  return (
    <div
      className={`${styles.reelCard} ${isActive ? styles.reelCardActive : ''}`.trim()}
      style={{background: placeholderBackground}}
      onClick={() => onActivate(reel.reelId)}
    >
      {showThumbnail && (
        <img
          src={reelThumbnailUrl}
          alt={`${reel.title} thumbnail`}
          className={styles.reelThumbnail}
          onError={() => onThumbnailError(reel.reelId)}
        />
      )}

      <button
        type="button"
        className={styles.reelPlayButton}
        aria-label={`Play ${reel.title}`}
        onClick={(event) => {
          event.stopPropagation();
          onPlay(reel);
        }}
      >
        <span
          className={styles.reelPlayIcon}
          style={{'--reel-play-icon-url': `url("${playFillIconUrl}")`} as React.CSSProperties}
          aria-hidden="true"
        />
      </button>

      <div className={`${styles.reelBadge} ${styles.reelDurationBadge}`}>
        {formatDuration(reel.durationSeconds)}
      </div>
      <div
        className={`${styles.reelBadge} ${styles.reelViewsBadge}`}
        aria-label={`${formattedViewCount} views`}
      >
        <span
          className={styles.reelViewsIcon}
          style={{'--reel-views-icon-url': `url("${viewFillIconUrl}")`} as React.CSSProperties}
          aria-hidden="true"
        />
        <span className={styles.reelViewsCount}>{formattedViewCount}</span>
      </div>

      <div className={styles.reelInfo}>
        <div className={styles.reelAuthorRow}>
          <div className={styles.reelAuthorAvatar}>
            {authorAvatarUrl && !authorAvatarFailed ? (
              <img
                src={authorAvatarUrl}
                alt=""
                onError={() => setAuthorAvatarFailed(true)}
              />
            ) : getDisplayInitial(reel.author.name)}
          </div>
          <span className={styles.reelAuthorName}>{reel.author.name}</span>
        </div>
        <p className={styles.reelTitle}>{reel.title}</p>
        <div className={styles.reelStatsRow} aria-label={`${formattedLikeCount} likes and ${formattedCommentCount} comments`}>
          <span className={`${styles.reelStatItem} ${reel.hasLiked ? styles.reelStatItemActive : ''}`.trim()}>
            <span
              className={styles.reelStatIcon}
              style={{'--reel-stat-icon-url': `url("${heartOutlineIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span>{formattedLikeCount}</span>
          </span>
          <span className={styles.reelStatItem}>
            <span
              className={styles.reelStatIcon}
              style={{'--reel-stat-icon-url': `url("${commentOutlineIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span>{formattedCommentCount}</span>
          </span>
        </div>
      </div>
    </div>
  );
});

const ScrollChevron: React.FC<{direction: 'left' | 'right'}> = ({direction}) => (
  <span
    className={styles.scrollButtonIcon}
    style={{'--home-scroll-arrow-icon-url': `url("${scrollArrowIconUrls[direction]}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const SearchIcon: React.FC = () => (
  <svg
    className={styles.reelSearchIcon}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M14.1667 14.1667L18 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="8.75"
      cy="8.75"
      r="5.75"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const LiveReelsSection: React.FC<LiveReelsSectionProps> = ({
  reels,
  reelsLoading,
  reelsError,
  selectedView,
  searchQuery,
  hasMoreReels,
  loadMoreReelsLoading,
  loadMoreReelsError,
  activeReelId,
  failedReelThumbnails,
  onRetry,
  onActivateReel,
  onPlayReel,
  onThumbnailError,
  onViewChange,
  onSearchChange,
  onLoadMoreReels,
  onCreateReel,
  activeTopicLabel,
}) => {
  const reelsScrollRef = useRef<HTMLDivElement>(null);
  const reelsScrollbarDragRef = useRef<{pointerId: number; thumbOffsetRatio: number} | null>(null);
  const reelsScrollerId = React.useId();
  const [reelsScrollbar, setReelsScrollbar] = React.useState({
    isScrollable: false,
    progress: 0,
    thumbLeft: 0,
    thumbWidth: 100,
  });
  const trimmedSearchQuery = searchQuery.trim();
  const hasSearchQuery = trimmedSearchQuery.length > 0;
  const disableDiscoveryControls = reelsLoading;
  const disableScrollControls = reelsLoading || reels.length === 0;
  const hasVisibleActiveReel = reels.some((reel) => reel.reelId === activeReelId);
  const firstVisibleReelId = reels[0]?.reelId || null;
  const showPaginationFooter = !reelsLoading && !reelsError && reels.length > 0 && (loadMoreReelsError || hasMoreReels);
  const showDefaultEmptyState = selectedView === 'all' && !hasSearchQuery && !activeTopicLabel;

  const updateReelsScrollbar = useCallback(() => {
    const scrollElement = reelsScrollRef.current;

    if (!scrollElement) {
      setReelsScrollbar({
        isScrollable: false,
        progress: 0,
        thumbLeft: 0,
        thumbWidth: 100,
      });
      return;
    }

    const maxScrollLeft = Math.max(scrollElement.scrollWidth - scrollElement.clientWidth, 0);
    const isScrollable = maxScrollLeft > 1;
    const thumbWidth = isScrollable
      ? Math.max((scrollElement.clientWidth / scrollElement.scrollWidth) * 100, 16)
      : 100;
    const maxThumbLeft = Math.max(100 - thumbWidth, 0);
    const progress = isScrollable
      ? (scrollElement.scrollLeft / maxScrollLeft) * 100
      : 0;
    const thumbLeft = isScrollable
      ? (scrollElement.scrollLeft / maxScrollLeft) * maxThumbLeft
      : 0;

    setReelsScrollbar({
      isScrollable,
      progress: Math.min(Math.max(progress, 0), 100),
      thumbLeft: Math.min(Math.max(thumbLeft, 0), maxThumbLeft),
      thumbWidth,
    });
  }, []);

  useEffect(() => {
    reelsScrollRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
    updateReelsScrollbar();
  }, [searchQuery, selectedView, updateReelsScrollbar]);

  useEffect(() => {
    updateReelsScrollbar();

    const scrollElement = reelsScrollRef.current;
    const resizeObserver = typeof ResizeObserver !== 'undefined' && scrollElement
      ? new ResizeObserver(updateReelsScrollbar)
      : null;

    if (scrollElement) {
      resizeObserver?.observe(scrollElement);
    }

    window.addEventListener('resize', updateReelsScrollbar);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateReelsScrollbar);
    };
  }, [reels.length, updateReelsScrollbar]);

  useEffect(() => {
    if (reelsLoading || reelsError || !firstVisibleReelId || hasVisibleActiveReel) {
      return;
    }

    onActivateReel(firstVisibleReelId);
  }, [firstVisibleReelId, hasVisibleActiveReel, onActivateReel, reelsError, reelsLoading]);

  const scrollReels = useCallback((direction: 'left' | 'right') => {
    if (!reelsScrollRef.current) {
      return;
    }

    const scrollAmount = 260;
    const nextScrollLeft = direction === 'left'
      ? reelsScrollRef.current.scrollLeft - scrollAmount
      : reelsScrollRef.current.scrollLeft + scrollAmount;

    reelsScrollRef.current.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth',
    });
  }, []);

  const scrollReelsToRatio = useCallback((ratio: number, behavior: ScrollBehavior = 'auto') => {
    const scrollElement = reelsScrollRef.current;

    if (!scrollElement) {
      return;
    }

    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;

    if (maxScrollLeft <= 0) {
      return;
    }

    scrollElement.scrollTo({
      left: Math.min(Math.max(ratio, 0), 1) * maxScrollLeft,
      behavior,
    });
  }, []);

  const getReelsScrollbarTrackRatio = useCallback((clientX: number, trackElement: HTMLElement) => {
    const trackRect = trackElement.getBoundingClientRect();
    const trackWidth = Math.max(trackRect.width, 1);

    return Math.min(Math.max((clientX - trackRect.left) / trackWidth, 0), 1);
  }, []);

  const scrollReelsToThumbPosition = useCallback((trackRatio: number, thumbOffsetRatio: number) => {
    const thumbWidthRatio = Math.min(Math.max(reelsScrollbar.thumbWidth / 100, 0), 1);
    const maxThumbLeftRatio = Math.max(1 - thumbWidthRatio, 0);

    if (maxThumbLeftRatio <= 0) {
      scrollReelsToRatio(0);
      return;
    }

    const nextThumbLeftRatio = Math.min(
      Math.max(trackRatio - thumbOffsetRatio, 0),
      maxThumbLeftRatio,
    );

    scrollReelsToRatio(nextThumbLeftRatio / maxThumbLeftRatio);
  }, [reelsScrollbar.thumbWidth, scrollReelsToRatio]);

  const handleReelsScrollbarPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!reelsScrollbar.isScrollable) {
      return;
    }

    const trackRatio = getReelsScrollbarTrackRatio(event.clientX, event.currentTarget);
    const thumbLeftRatio = reelsScrollbar.thumbLeft / 100;
    const thumbWidthRatio = Math.min(Math.max(reelsScrollbar.thumbWidth / 100, 0), 1);
    const thumbRightRatio = thumbLeftRatio + thumbWidthRatio;
    const pointerStartedOnThumb = trackRatio >= thumbLeftRatio && trackRatio <= thumbRightRatio;
    const thumbOffsetRatio = pointerStartedOnThumb
      ? trackRatio - thumbLeftRatio
      : thumbWidthRatio / 2;

    event.preventDefault();
    reelsScrollbarDragRef.current = {
      pointerId: event.pointerId,
      thumbOffsetRatio,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    scrollReelsToThumbPosition(trackRatio, thumbOffsetRatio);
  }, [
    getReelsScrollbarTrackRatio,
    reelsScrollbar.isScrollable,
    reelsScrollbar.thumbLeft,
    reelsScrollbar.thumbWidth,
    scrollReelsToThumbPosition,
  ]);

  const handleReelsScrollbarPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const activeDrag = reelsScrollbarDragRef.current;

    if (
      !activeDrag
      || activeDrag.pointerId !== event.pointerId
      || !event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      return;
    }

    event.preventDefault();
    scrollReelsToThumbPosition(
      getReelsScrollbarTrackRatio(event.clientX, event.currentTarget),
      activeDrag.thumbOffsetRatio,
    );
  }, [getReelsScrollbarTrackRatio, scrollReelsToThumbPosition]);

  const handleReelsScrollbarPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const activeDrag = reelsScrollbarDragRef.current;

    if (!activeDrag || activeDrag.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    reelsScrollbarDragRef.current = null;
  }, []);

  const handleReelsScrollbarKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const scrollElement = reelsScrollRef.current;

    if (!scrollElement) {
      return;
    }

    const scrollStep = 260;
    const pageStep = Math.max(scrollElement.clientWidth * 0.8, scrollStep);
    const keyScrollBy: Record<string, number> = {
      ArrowLeft: -scrollStep,
      ArrowRight: scrollStep,
      PageUp: -pageStep,
      PageDown: pageStep,
    };

    if (event.key === 'Home') {
      event.preventDefault();
      scrollElement.scrollTo({left: 0, behavior: 'smooth'});
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      scrollElement.scrollTo({
        left: scrollElement.scrollWidth - scrollElement.clientWidth,
        behavior: 'smooth',
      });
      return;
    }

    const nextScrollBy = keyScrollBy[event.key];

    if (typeof nextScrollBy !== 'number') {
      return;
    }

    event.preventDefault();
    scrollElement.scrollBy({
      left: nextScrollBy,
      behavior: 'smooth',
    });
  }, []);

  const renderEmptyMessage = () => {
    if (activeTopicLabel && hasSearchQuery) {
      return `No reels about ${activeTopicLabel} match "${trimmedSearchQuery}" in this view.`;
    }

    if (activeTopicLabel) {
      return `No reels are currently available for ${activeTopicLabel}.`;
    }

    if (selectedView === 'featured' && !hasSearchQuery) {
      return 'No featured reels yet. Switch back to All to browse every uploaded reel.';
    }

    if (hasSearchQuery) {
      return `No reels match "${trimmedSearchQuery}" in this view.`;
    }

    if (selectedView !== 'all') {
      return 'No reels match the current discovery view.';
    }

    return 'No live reels yet. Uploaded highlight reels will appear here as creators start sharing videos.';
  };

  return (
    <div id="reels" className={styles.reelsSection}>
      <div className={styles.sectionInner}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeading}>
            <h2 className={`${styles.sectionTitle} ${styles.reelsHeadingTitle}`}>
              <span
                className={styles.reelsHeadingIcon}
                style={{'--reels-heading-icon-url': `url("${reelsHeadingIconUrl}")`} as React.CSSProperties}
                aria-hidden="true"
              />
              <span>Reels</span>
            </h2>
            <p className={styles.sectionSubtitle}>Watch short highlights from creators.</p>
          </div>
          <div className={styles.sectionActions}>
            <div className={styles.reelDiscoveryToolbar}>
              <label className={styles.reelSearch}>
                <SearchIcon />
                <input
                  type="search"
                  value={searchQuery}
                  className={styles.reelSearchInput}
                  placeholder="Search reels or creators"
                  aria-label="Search reels or creators"
                  autoComplete="off"
                  disabled={disableDiscoveryControls}
                  onChange={(event) => onSearchChange(event.target.value)}
                />
              </label>
              <div className={styles.reelFilters} aria-label="Reel discovery views" role="group">
                {REEL_FILTER_OPTIONS.map((filterOption) => (
                  <button
                    key={filterOption.id}
                    type="button"
                    className={`${styles.reelFilterButton} ${selectedView === filterOption.id ? styles.reelFilterButtonActive : ''}`.trim()}
                    aria-pressed={selectedView === filterOption.id}
                    disabled={disableDiscoveryControls}
                    onClick={() => onViewChange(filterOption.id)}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.reelScrollActions}>
              <button
                type="button"
                className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.scrollButton}`}
                aria-label="Scroll reels left"
                disabled={disableScrollControls}
                onClick={() => scrollReels('left')}
              >
                <ScrollChevron direction="left" />
              </button>
              <button
                type="button"
                className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.scrollButton}`}
                aria-label="Scroll reels right"
                disabled={disableScrollControls}
                onClick={() => scrollReels('right')}
              >
                <ScrollChevron direction="right" />
              </button>
            </div>
          </div>
        </div>

        {reelsLoading && (
          <div className={styles.sectionMessage}>Loading live reels...</div>
        )}

        {reelsError && (
          <div className={styles.sectionErrorRow}>
            <p className={styles.sectionErrorText}>
              <HomeErrorMessage message={reelsError} />
            </p>
            <button type="button" className={styles.primaryAction} onClick={onRetry}>
              Retry
            </button>
          </div>
        )}

        {!reelsLoading && !reelsError && reels.length === 0 && showDefaultEmptyState && (
          <div
            className={styles.reelsEmptyState}
            style={{'--reels-empty-background-url': `url("${reelsEmptyBackgroundUrl}")`} as React.CSSProperties}
          >
            <div className={styles.reelsEmptyContent}>
              <span className={styles.reelsEmptyBadge}>
                <span className={styles.reelsEmptyDot} aria-hidden="true" />
                No reels yet
              </span>
              <h3 className={styles.reelsEmptyTitle}>No reels yet</h3>
              <p className={styles.reelsEmptyDescription}>
                When creators publish short videos, they'll appear here.
              </p>
              <div className={styles.reelsEmptyActions}>
                <button
                  type="button"
                  className={`${styles.reelsEmptyButton} ${styles.reelsEmptyButtonPrimary}`}
                  onClick={onCreateReel}
                >
                  <span
                    className={styles.reelsEmptyButtonIcon}
                    style={{'--reels-empty-button-icon-url': `url("${reelsEmptyCreateIconUrl}")`} as React.CSSProperties}
                    aria-hidden="true"
                  />
                  Create Reel
                </button>
              </div>
            </div>
          </div>
        )}

        {!reelsLoading && !reelsError && reels.length === 0 && !showDefaultEmptyState && (
          <div className={styles.sectionMessage}>
            {renderEmptyMessage()}
          </div>
        )}

        {!reelsLoading && !reelsError && reels.length > 0 && (
          <>
            <div
              id={reelsScrollerId}
              ref={reelsScrollRef}
              className={styles.reelsScroller}
              onScroll={updateReelsScrollbar}
            >
              {reels.map((reel, index) => (
                <ReelCard
                  key={reel.reelId}
                  reel={reel}
                  index={index}
                  isActive={activeReelId === reel.reelId}
                  hasThumbnailError={Boolean(failedReelThumbnails[reel.reelId])}
                  onActivate={onActivateReel}
                  onPlay={onPlayReel}
                  onThumbnailError={onThumbnailError}
                />
              ))}
            </div>
            {reelsScrollbar.isScrollable ? (
              <div
                className={styles.reelsCustomScrollbar}
                role="scrollbar"
                aria-label="Reels scroll position"
                aria-controls={reelsScrollerId}
                aria-orientation="horizontal"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(reelsScrollbar.progress)}
                aria-valuetext={`${Math.round(reelsScrollbar.progress)}% scrolled`}
                tabIndex={0}
                onKeyDown={handleReelsScrollbarKeyDown}
                onPointerDown={handleReelsScrollbarPointerDown}
                onPointerMove={handleReelsScrollbarPointerMove}
                onPointerUp={handleReelsScrollbarPointerEnd}
                onPointerCancel={handleReelsScrollbarPointerEnd}
              >
                <span
                  className={styles.reelsCustomScrollbarThumb}
                  style={{
                    '--reels-scrollbar-thumb-left': `${reelsScrollbar.thumbLeft}%`,
                    '--reels-scrollbar-thumb-width': `${reelsScrollbar.thumbWidth}%`,
                  } as React.CSSProperties}
                />
              </div>
            ) : null}

            {showPaginationFooter && (
              <div className={styles.reelsPaginationFooter}>
                {loadMoreReelsError ? (
                  <div className={styles.reelsPaginationErrorRow}>
                    <p className={styles.reelsPaginationErrorText}>{loadMoreReelsError}</p>
                    <button
                      type="button"
                      className={styles.primaryAction}
                      disabled={loadMoreReelsLoading}
                      onClick={onLoadMoreReels}
                    >
                      {loadMoreReelsLoading ? 'Loading more reels...' : 'Retry loading more'}
                    </button>
                  </div>
                ) : (
                  <div className={styles.reelsPaginationRow}>
                    <button
                      type="button"
                      className={styles.primaryAction}
                      disabled={loadMoreReelsLoading}
                      onClick={onLoadMoreReels}
                    >
                      {loadMoreReelsLoading ? 'Loading more reels...' : 'Load more reels'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(LiveReelsSection);
